import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";
import { normalizeSections } from "../Draft/ProposalDraftStep";
import Tooltip from "../../../Project Page/Tooltip";
import api from "../../../../../chat/api";
import docIcon from "./icons/doc.png";
import pdfIcon from "./icons/pdf.png";
import zipIcon from "./icons/zip.png";

// Empty sections listed inline before collapsing the rest into "+N more".
const MAX_VISIBLE_EMPTY = 5;

const formatNumber = (num) => String(num ?? "").replace(/\.+$/, "");

const isDone = (node) =>
  !!node.markAsDone || !!(node.content && node.content.trim());

// "4. Management Approach" for a section, "3.2 Security Plan" for a subsection
// (subsections, whose number has a dot, get no trailing period).
const nodeLabel = (node) => {
  const num = formatNumber(node.number);
  const isSub = String(node.number ?? "").includes(".");
  if (!num) return node.title || "Untitled";
  return isSub ? `${num} ${node.title || ""}`.trim() : `${num}. ${node.title || ""}`.trim();
};

// "a", "a and b", "a, b and c"
const joinAnd = (arr) =>
  arr.length <= 1
    ? arr[0] || ""
    : arr.length === 2
    ? `${arr[0]} and ${arr[1]}`
    : `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;

const sanitizeFileName = (name) =>
  (name || "Proposal").trim().replace(/[\\/:*?"<>|]+/g, "_") || "Proposal";

const escapeHtml = (s) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );

// Build a self-contained, print-ready HTML document; PDF is produced via the
// browser's print engine in a new tab that auto-prints.
const buildPrintHtml = (sections, title) => {
  const tocRows = sections
    .map((s) => {
      const n = formatNumber(s.number);
      const main = `<div class="toc-item toc-main"><a href="#sec-${s.id}">${
        n ? n + " " : ""
      }${escapeHtml(s.title || "Untitled")}</a></div>`;
      const subs = (s.children || [])
        .map((c) => {
          const cn = formatNumber(c.number);
          return `<div class="toc-item toc-sub"><a href="#sec-${c.id}">${
            cn ? cn + " " : ""
          }${escapeHtml(c.title || "")}</a></div>`;
        })
        .join("");
      return main + subs;
    })
    .join("");

  const body = sections
    .map((s) => {
      const n = formatNumber(s.number);
      const head = `<h1 id="sec-${s.id}">${n ? n + " " : ""}${escapeHtml(
        s.title || "Untitled"
      )}</h1>`;
      const subs = (s.children || [])
        .map((c) => {
          const cn = formatNumber(c.number);
          return `<h2 id="sec-${c.id}">${cn ? cn + " " : ""}${escapeHtml(
            c.title || ""
          )}</h2>${c.content || ""}`;
        })
        .join("");
      return `<section class="doc-section">${head}${s.content || ""}${subs}</section>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap" rel="stylesheet" />
<style>
  /* margin:0 so the browser draws no header/footer (URL, date, page #). Per-page
     top/bottom margins are reserved by the repeating .print-page thead/tfoot
     spacers, and side margins by the .print-body cell padding — these apply to
     every page, including continuation pages of a long section. */
  @page { size: A4; margin: 0; }
  body { font-family: 'Outfit', Arial, sans-serif; color: #374151; font-size: 14px; line-height: 1.7; margin: 0; }
  .print-page { width: 100%; border-collapse: collapse; table-layout: auto; margin: 0; }
  .print-page > thead { display: table-header-group; }
  .print-page > tfoot { display: table-footer-group; }
  .print-page > thead > tr > td, .print-page > tfoot > tr > td { height: 16mm; border: 0; padding: 0; }
  .print-page > tbody > tr { break-inside: auto; }
  .print-page > tbody > tr > td { padding: 0 16mm; border: 0; vertical-align: top; break-inside: auto; }
  .title-page { text-align: center; padding-top: 4cm; page-break-after: always; }
  .title-page .t { font-size: 26pt; font-weight: 700; color: #111827; }
  .toc { page-break-after: always; }
  .toc h2 { font-size: 18pt; margin: 0 0 14px; color: #111827; }
  .toc-item { margin: 5px 0; }
  .toc-item a { color: #111827; text-decoration: none; }
  .toc-main a { font-weight: 700; }
  .toc-sub { padding-left: 18px; font-size: 12px; color: #374151; }
  .doc-section { page-break-before: always; }
  h1 { font-size: 24px; font-weight: 700; color: #111827; margin: 18px 0 12px; break-after: avoid; }
  h2 { font-size: 20px; font-weight: 700; color: #111827; margin: 16px 0 10px; break-after: avoid; }
  h3 { font-size: 16px; font-weight: 600; color: #111827; margin: 14px 0 8px; break-after: avoid; }
  h4 { font-size: 15px; font-weight: 700; color: #111827; margin: 14px 0 8px; break-after: avoid; }
  p { margin: 0 0 10px; orphans: 3; widows: 3; }
  ul, ol { margin: 8px 0 12px; padding-left: 24px; }
  ul { list-style: disc; } ol { list-style: decimal; }
  li { margin: 4px 0; break-inside: avoid; }
  li p { margin: 0; }
  strong { font-weight: 700; } em { font-style: italic; }
  img { max-width: 100%; height: auto; break-inside: avoid; }
  table { border-collapse: collapse; width: 100%; margin: 16px 0; table-layout: fixed; }
  thead { display: table-header-group; }
  tr, th, td { break-inside: avoid; }
  th, td { border: 1px solid #d1d5db; padding: 10px; text-align: left; vertical-align: top; word-wrap: break-word; }
  th { background: #f9fafb; font-weight: 700; color: #111827; }
  a { color: #A04A97; }
  .action-bar { position: fixed; top: 16px; right: 16px; display: flex; gap: 10px; z-index: 9999; }
  .action-btn { background: #C95EBE; color: #fff; border: none; padding: 9px 18px; border-radius: 6px; font-family: 'Outfit', sans-serif; font-weight: 500; font-size: 14px; cursor: pointer; }
  .action-btn.secondary { background: #fff; color: #374151; border: 1px solid #D1D5DB; }
  @media print { .no-print { display: none !important; } }
</style>
</head>
<body>
  <div class="action-bar no-print">
    <button class="action-btn secondary" onclick="window.close()">Close</button>
    <button class="action-btn" onclick="window.print()">Save as PDF</button>
  </div>
  <table class="print-page">
    <thead><tr><td></td></tr></thead>
    <tbody><tr><td>
      <div class="title-page">
        <div class="t">${escapeHtml(title)}</div>
      </div>
      <div class="toc">
        <h2>Table of Contents</h2>
        ${tocRows}
      </div>
      ${body}
    </td></tr></tbody>
    <tfoot><tr><td></td></tr></tfoot>
  </table>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 500); };</script>
</body>
</html>`;
};

// Open the print-ready document in a new tab (user-gesture safe).
// Retired in favour of the server-side WeasyPrint PDF; kept for rollback.
// eslint-disable-next-line no-unused-vars
const openProposalPrint = (sections, title) => {
  const url = URL.createObjectURL(
    new Blob([buildPrintHtml(sections, title)], {
      type: "text/html;charset=utf-8",
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const CheckCircle = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
    <circle cx="10" cy="10" r="10" fill="#22A06B" />
    <path
      d="M5.8 10.4l2.6 2.6L14.2 7"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const XCircle = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="flex-shrink-0 mt-0.5">
    <circle cx="10" cy="10" r="10" fill="#E5484D" />
    <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" className="flex-shrink-0 mt-[1px]">
    <circle cx="10" cy="10" r="8" stroke="#9CA3AF" strokeWidth="1.4" />
    <path d="M10 9v4.5M10 6.5h.01" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CheckMark = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 6.2l2.2 2.2L9.5 3.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Divider = () => (
  <div
    className="h-px w-full my-6"
    style={{
      background:
        "linear-gradient(90deg, #E6E2EA 0%, #EFEDF1 50%, rgba(239,237,241,0) 100%)",
    }}
  />
);

const FILE_ICON = { docx: docIcon, pdf: pdfIcon, zip: zipIcon };

const FileBadge = ({ type }) => (
  <img
    src={FILE_ICON[type]}
    alt={`${type} icon`}
    className="w-7 h-7 flex-shrink-0 object-contain"
  />
);

const RadioDot = ({ selected }) => (
  <span
    className={`flex items-center justify-center w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 ${
      selected ? "border-[#C95EBE]" : "border-gray-300"
    }`}
  >
    {selected && <span className="w-2 h-2 rounded-full bg-[#C95EBE]" />}
  </span>
);

const FORMATS = [
  {
    id: "docx",
    label: "DOCX",
    description:
      "Full editable Microsoft Word document for further review, collaboration, and customization.",
  },
  {
    id: "pdf",
    label: "PDF",
    description:
      "Print-ready PDF — ideal for final submission and sharing with evaluators.",
  },
  {
    id: "zip",
    label: "ZIP FILE",
    description:
      "Bundle the proposal as a single .zip archive containing the editable Word (DOCX) document.",
  },
];

// Final step: review section completeness, pick a format, then export.
const ProposalExportStep = ({
  apiSections,
  projectName,
  folderId,
  onExportControlsChange,
}) => {
  const { projectId } = useParams();
  const fid = folderId || projectId;
  const token = localStorage.getItem("token");

  const [format, setFormat] = useState("docx");
  const [fileName, setFileName] = useState(projectName || "");
  const [exporting, setExporting] = useState(false);

  // Documents enabled in Setup, shown read-only (already in the download).
  const [availableDocs, setAvailableDocs] = useState([]);

  useEffect(() => {
    if (!fid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(
          `api/v1/rfp/folders/${fid}/proposal-config/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        setAvailableDocs(
          (res.data?.proposal_documents || []).filter((d) => d.enabled)
        );
      } catch (error) {
        console.error("Error loading proposal documents:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fid, token]);


  // Keep the filename in sync with the project name until the user edits it.
  const [nameTouched, setNameTouched] = useState(false);
  useEffect(() => {
    if (!nameTouched && projectName) setFileName(projectName);
  }, [projectName, nameTouched]);

  // Flatten sections + subsections; "drafted" = has content or marked done.
  const { drafted, empty } = useMemo(() => {
    const sections =
      apiSections && apiSections.length ? normalizeSections(apiSections) : [];
    const flat = [];
    sections.forEach((s) => {
      flat.push(s);
      (s.children || []).forEach((c) => flat.push(c));
    });
    return {
      drafted: flat.filter(isDone),
      empty: flat.filter((n) => !isDone(n)),
    };
  }, [apiSections]);

  const selectedFormat = FORMATS.find((f) => f.id === format) || FORMATS[0];


  const handleExport = useCallback(async () => {
    const fmt = FORMATS.find((f) => f.id === format) || FORMATS[0];
    if (fmt.disabled || exporting) return;
    if (!fid) {
      toast.error("Missing folder reference — cannot export.");
      return;
    }

    const safeName = sanitizeFileName(fileName);

    // PDF now renders server-side (templated, via WeasyPrint) — it falls through
    // to the generic backend download below with ?type=pdf, same as DOCX. The
    // legacy client-side print path (openProposalPrint/buildPrintHtml) is retired.

    // ZIP wraps the backend DOCX (Word document) in a .zip archive.
    if (format === "zip") {
      setExporting(true);
      try {
        const response = await api.get(
          `api/v1/rfp/folders/${fid}/generate-proposal/download/?type=docx`,
          { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
        );
        const docxBlob = new Blob([response.data]);
        const JSZip = (await import("jszip")).default;
        const zip = new JSZip();
        zip.file(`${safeName}.docx`, docxBlob);
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `${safeName}.zip`);
        toast.success("ZIP downloaded successfully!");
      } catch (error) {
        console.error("Error exporting proposal:", error);
        toast.error("Failed to export the proposal. Please try again.");
      } finally {
        setExporting(false);
      }
      return;
    }

    setExporting(true);
    try {
      const response = await api.get(
        `api/v1/rfp/folders/${fid}/generate-proposal/download/?type=${format}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${safeName}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error("Error exporting proposal:", error);
      toast.error("Failed to export the proposal. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [format, fileName, fid, token, exporting]);

  // Surface the export action up so the button can live in the step-bar row.
  const exportDisabled =
    exporting || selectedFormat.disabled || !drafted.length;
  useEffect(() => {
    if (!onExportControlsChange) return undefined;
    onExportControlsChange({ onExport: handleExport, exporting, disabled: exportDisabled });
    return () => onExportControlsChange(null);
  }, [onExportControlsChange, handleExport, exporting, exportDisabled]);

  return (
    <div
      className="h-full overflow-y-auto custom-scrollbar pb-8"
      style={{ backgroundColor: "#FAF9FB" }}
    >
      <div className="w-full max-w-[840px] mx-auto px-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-[#1A1A1A] font-outfit text-[20px] font-semibold">
            Export Proposal
          </h2>
          <p className="text-[#767676] font-outfit text-[14px] mt-1 max-w-[640px]">
            Conduct a final review of your proposal to ensure all requirements
            have been met. Once validated, select an export format to generate
            the final document.
          </p>
        </div>
      </div>

      <Divider />

      {/* Section Status */}
      <div>
        <h3 className="text-[#1A1A1A] font-outfit text-[16px] font-semibold">
          Section Status
        </h3>
        <p className="text-[#767676] font-outfit text-[14px] mt-0.5">
          Review which sections are complete before generating your document.
        </p>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mt-3">
          <div className="flex items-center gap-2.5">
            <CheckCircle />
            <p className="font-outfit text-[15px] text-[#3F3F46]">
              <span className="font-medium text-[#000000B3]">
                {drafted.length} section{drafted.length === 1 ? "" : "s"} drafted
              </span>
            </p>
          </div>

          {empty.length > 0 ? (
            <div className="flex items-start gap-2.5 mt-4">
              <XCircle />
              <p className="font-outfit text-[15px] text-[#3F3F46] leading-relaxed">
                <span className="font-medium text-[#000000B3]">
                  {empty.length} section{empty.length === 1 ? "" : "s"} empty
                </span>{" "}
                <span className="text-[#767676]">
                  &nbsp;-&nbsp;
                  {empty.length > MAX_VISIBLE_EMPTY ? (
                    <>
                      {empty.slice(0, MAX_VISIBLE_EMPTY).map(nodeLabel).join(", ")}
                      {", "}
                      <Tooltip
                        placement="left"
                        content={
                          <span className="block text-left">
                            {empty.slice(MAX_VISIBLE_EMPTY).map((n) => (
                              <span key={n.id} className="block">
                                {nodeLabel(n)}
                              </span>
                            ))}
                          </span>
                        }
                      >
                        <span className="text-[#C95EBE] font-medium underline decoration-dotted underline-offset-2 cursor-default">
                          +{empty.length - MAX_VISIBLE_EMPTY} more
                        </span>
                      </Tooltip>
                    </>
                  ) : (
                    joinAnd(empty.map(nodeLabel))
                  )}
                </span>
              </p>
            </div>
          ) : (
            drafted.length > 0 && (
              <p className="font-outfit text-[13px] text-[#458764] mt-3 pl-[30px]">
                All sections have content — your proposal is ready to export.
              </p>
            )
          )}
        </div>
      </div>

      <Divider />

      {/* Export Settings */}
      <div>
        <h3 className="text-[#1A1A1A] font-outfit text-[16px] font-semibold">
          Export Settings
        </h3>
        <p className="text-[#767676] font-outfit text-[14px] mt-0.5">
          Select a format, choose optional documents and export your final
          proposal document.
        </p>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-3">
        
          <div className="flex items-center border-b border-gray-200 pb-2.5 transition-colors focus-within:border-[#C95EBE]">
            <input
              type="text"
              value={fileName}
              onChange={(e) => {
                setNameTouched(true);
                setFileName(e.target.value);
              }}
              placeholder="Document file name"
              className="w-full border-0 bg-transparent font-outfit !text-[16px] leading-normal text-[#3F3F46] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0"
            />
          </div>

          {/* Format options */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mt-5">
            {FORMATS.map((f) => {
              const selected = format === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  disabled={f.disabled}
                  onClick={() => setFormat(f.id)}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border text-left transition-colors ${
                    selected
                      ? "border-[#A04A9759] bg-[#A04A9712]"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  } ${f.disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <FileBadge type={f.id} />
                    <span className="font-outfit text-[15px] font-medium text-[#1A1A1A] truncate">
                      {f.label}
                      {f.disabled && (
                        <span className="ml-1.5 text-[11px] font-normal text-[#9CA3AF]">
                          (soon)
                        </span>
                      )}
                    </span>
                  </span>
                  <RadioDot selected={selected} />
                </button>
              );
            })}
          </div>

          {/* Format description */}
          <div className="flex items-start gap-2 mt-4">
            <InfoIcon />
            <p className="font-outfit text-[13px] text-[#767676] leading-relaxed">
              {selectedFormat.description}
            </p>
          </div>
        </div>
      </div>

      {/* Additional Documents */}
      <div className="mt-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h4 className="text-[#1A1A1A] font-outfit text-[15px] font-semibold mb-1">
            Additional Documents
          </h4>
          <p className="font-outfit text-[13px] text-[#9CA3AF] mb-4">
            These documents are included in your proposal download. Manage them in
            the Setup step.
          </p>
          {availableDocs.length === 0 ? (
            <p className="font-outfit text-[14px] text-[#9CA3AF]">
              No additional documents in this proposal. Add them in the Setup step.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3.5">
              {availableDocs.map((d) => (
                <div
                  key={d.id}
                  title={d.description || d.name}
                  className="flex items-center gap-2.5 select-none"
                >
                  <span className="flex items-center justify-center w-[18px] h-[18px] rounded border bg-[#C95EBE] border-[#C95EBE] flex-shrink-0">
                    <CheckMark />
                  </span>
                  <span className="font-outfit text-[14px] text-[#3F3F46] truncate">
                    {d.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProposalExportStep;
